import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useLocation } from "react-router-dom";
import {
  X,
  Send,
  Loader2,
  Trash2,
  Minimize2,
  Check,
  Sparkles,
  MessageSquare,
  History,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { API, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const MAYA_AVATAR_URL = "/maya-avatar.png";

// Variant catalog — each entry maps to a pre-generated PNG in /frontend/public.
// The "happy" default is the base portrait; other variants are produced by
// Nano Banana with the same character identity (red hair, blue polo, badge)
// but different facial expression / gesture. See
// /app/backend/scripts/generate_maya_variants.py for the generation script.
const MAYA_AVATAR_VARIANTS = {
  happy: MAYA_AVATAR_URL,
  alert: "/maya-avatar-alert.png",
  celebrating: "/maya-avatar-celebrating.png",
  thinking: "/maya-avatar-thinking.png",
};

const SUGGESTIONS = [
  "Quais vagas estão abertas?",
  "Quais entrevistas eu tenho hoje?",
  "Quais candidatos têm score acima de 80?",
  "Como envio um WhatsApp para um candidato?",
];

const TOOL_LABELS = {
  search_candidates: "Buscar candidatos",
  list_jobs: "Listar vagas",
  list_interviews: "Listar entrevistas",
  schedule_interview: "📅 Agendar entrevista",
  change_candidate_status: "🔄 Mudar status",
  send_whatsapp_to_candidate: "💬 Enviar WhatsApp",
  send_email_to_candidate: "✉️ Enviar e-mail",
  reanalyze_candidate: "🤖 Reanalisar com IA",
};

const STATUS_TONE = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  auto_confirmed: "bg-sky-100 text-sky-800 border-sky-200",
  executed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  failed: "bg-rose-100 text-rose-800 border-rose-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
};

const STATUS_LABEL = {
  pending: "Aguarda confirmação",
  auto_confirmed: "Executado",
  executed: "Executado",
  failed: "Falhou",
  cancelled: "Cancelado",
};

function renderMarkdown(text) {
  if (!text) return "";
  let html = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  html = html.replace(/\*\*([^*]+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/^\s*-\s+/gm, "• ");
  html = html.replace(
    /`([^`]+)`/g,
    '<code class="px-1 py-0.5 rounded bg-slate-100 text-slate-800 text-xs">$1</code>',
  );
  html = html.replace(/\n/g, "<br/>");
  return html;
}

// Per-variant entry animation — fires every time the variant changes.
// `celebrating` does a bouncy pop, `alert` does a quick horizontal shake,
// `thinking` does a gentle nod, `happy` is just a clean fade in.
const VARIANT_ANIMATIONS = {
  happy: {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.25, ease: "easeOut" },
  },
  thinking: {
    initial: { opacity: 0, rotate: -8, scale: 0.92 },
    animate: { opacity: 1, rotate: [0, -4, 0, 3, 0], scale: 1 },
    transition: { duration: 0.6, ease: "easeOut", times: [0, 0.3, 0.5, 0.8, 1] },
  },
  alert: {
    initial: { opacity: 0, scale: 0.85 },
    animate: { opacity: 1, x: [0, -3, 3, -2, 2, 0], scale: 1 },
    transition: { duration: 0.5, ease: "easeOut" },
  },
  celebrating: {
    initial: { opacity: 0, scale: 0.7, y: 8 },
    animate: { opacity: 1, scale: [1.15, 1], y: [0, -6, 0] },
    transition: {
      duration: 0.55,
      ease: "easeOut",
      scale: { type: "spring", stiffness: 350, damping: 12 },
    },
  },
};

const MayaAvatar = ({ size = 28, className = "", variant = "happy" }) => {
  const src = MAYA_AVATAR_VARIANTS[variant] || MAYA_AVATAR_URL;
  const anim = VARIANT_ANIMATIONS[variant] || VARIANT_ANIMATIONS.happy;

  return (
    <div
      className={`relative rounded-full overflow-hidden shrink-0 shadow-sm ring-2 ring-white ${className}`}
      style={{ width: size, height: size }}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.img
          key={variant}
          src={src}
          alt="Maya"
          width={size}
          height={size}
          data-variant={variant}
          loading="eager"
          initial={anim.initial}
          animate={anim.animate}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={anim.transition}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ willChange: "transform, opacity" }}
        />
      </AnimatePresence>
    </div>
  );
};

const ActionCard = ({ action, onConfirm, onCancel, busy }) => {
  const tone = STATUS_TONE[action.status] || STATUS_TONE.pending;
  const isPending = action.status === "pending";

  return (
    <div
      data-testid={`maya-action-${action.id}`}
      className="bg-white border border-violet-200 rounded-xl p-3 mt-2 shadow-sm"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-wider font-bold text-violet-700">
          Ação proposta
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${tone}`}>
          {STATUS_LABEL[action.status] || action.status}
        </span>
      </div>
      <div className="text-[11px] font-bold text-slate-900 mb-1">
        {TOOL_LABELS[action.tool_name] || action.tool_name}
      </div>
      <div
        className="text-xs text-slate-700 leading-snug"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: renderMarkdown(action.preview) }}
      />
      {action.error && (
        <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1 flex items-start gap-1">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          {action.error}
        </div>
      )}
      {isPending && (
        <div className="flex items-center gap-2 mt-3">
          <Button
            size="sm"
            onClick={() => onConfirm(action.id)}
            disabled={busy}
            data-testid={`maya-confirm-${action.id}`}
            className="bg-emerald-500 hover:bg-emerald-600 text-white h-7 text-[11px] px-3 flex-1"
          >
            {busy ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <>
                <Check className="w-3 h-3 mr-1" />
                Confirmar
              </>
            )}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onCancel(action.id)}
            disabled={busy}
            data-testid={`maya-cancel-${action.id}`}
            className="h-7 text-[11px] px-3"
          >
            <X className="w-3 h-3 mr-1" />
            Cancelar
          </Button>
        </div>
      )}
    </div>
  );
};

const AIAssistantWidget = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("chat");

  // Override the tab title with "Maya Copilot · GridRecruiter" while the chat
  // is open — only effects when `open` is true; closing restores the page's
  // own useDocumentTitle.
  useDocumentTitle("Maya Copilot", { ready: open });
  const [messages, setMessages] = useState([]);
  const [actionsById, setActionsById] = useState({});
  const [actionsList, setActionsList] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [busyAction, setBusyAction] = useState("");
  // Proactive insight — silent candidates (no-show risk)
  const [silentRisk, setSilentRisk] = useState(null);
  const [insightDismissed, setInsightDismissed] = useState(() => {
    try {
      return sessionStorage.getItem("maya_silent_insight_dismissed") === "1";
    } catch {
      return false;
    }
  });
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // ── History + actions + proactive insight load on first open ──────
  useEffect(() => {
    if (!open || historyLoaded) return;
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/assistant/history`).catch(() => ({ data: [] })),
      axios.get(`${API}/assistant/actions?limit=50`).catch(() => ({ data: [] })),
      // Probe for silent candidates — never fails the chat load
      axios
        .get(`${API}/interviews/at-risk?hours_window=48`)
        .catch(() => ({ data: { at_risk: [], count: 0 } })),
    ])
      .then(([h, a, r]) => {
        if (cancelled) return;
        const msgs = h.data || [];
        const acts = a.data || [];
        setMessages(msgs);
        setActionsList(acts);
        const map = {};
        acts.forEach((act) => (map[act.id] = act));
        setActionsById(map);
        setSilentRisk(r.data || { at_risk: [], count: 0 });
      })
      .finally(() => {
        if (!cancelled) setHistoryLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, historyLoaded]);

  const dismissInsight = () => {
    setInsightDismissed(true);
    try {
      sessionStorage.setItem("maya_silent_insight_dismissed", "1");
    } catch {
      /* ignore */
    }
  };

  const askMayaToFollowUp = (candidate) => {
    // Pre-populate the input with a Maya nudge — user reviews + sends
    const firstName = (candidate.candidate_name || "candidato").split(" ")[0];
    const phone = candidate.candidate_phone || "";
    setInput(
      `Manda um WhatsApp pro ${firstName}${phone ? ` (${phone})` : ""} avisando que a entrevista de ${candidate.job_title} está marcada pra ${new Date(candidate.starts_at).toLocaleString("pt-BR")} e pedindo pra confirmar.`,
    );
    setTab("chat");
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  useEffect(() => {
    if (!open || tab !== "chat") return;
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages, open, sending, tab]);

  useEffect(() => {
    if (open && tab === "chat" && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open, tab]);

  // ── Avatar variant logic ─────────────────────────────────────────────
  // Priority order:
  //   1. `sending` → thinking (she's processing your request)
  //   2. Most recent assistant message has an executed MUTATION action
  //      (schedule_interview, send_whatsapp, etc.) → celebrating
  //   3. Last action failed → alert
  //   4. Silent-risk insight is active and not dismissed → alert
  //   5. Otherwise → happy
  const mutationTools = new Set([
    "schedule_interview",
    "send_whatsapp_to_candidate",
    "send_email_to_candidate",
    "change_candidate_status",
    "reanalyze_candidate",
  ]);

  const lastAssistantWithAction = (() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role !== "assistant" || !m.action_id) continue;
      const act = actionsById[m.action_id];
      if (act) return act;
    }
    return null;
  })();

  let headerVariant = "happy";
  if (sending) {
    headerVariant = "thinking";
  } else if (lastAssistantWithAction) {
    const { tool_name: tool, status } = lastAssistantWithAction;
    if (status === "failed") {
      headerVariant = "alert";
    } else if (
      (status === "executed" || status === "auto_confirmed") &&
      mutationTools.has(tool)
    ) {
      headerVariant = "celebrating";
    }
  }
  if (
    headerVariant === "happy" &&
    silentRisk &&
    silentRisk.count > 0 &&
    !insightDismissed
  ) {
    headerVariant = "alert";
  }

  const variantForMessage = (m) => {
    if (!m.action_id) return "happy";
    const act = actionsById[m.action_id];
    if (!act) return "happy";
    if (act.status === "failed") return "alert";
    if (act.status === "pending") return "thinking";
    if (
      (act.status === "executed" || act.status === "auto_confirmed") &&
      mutationTools.has(act.tool_name)
    ) {
      return "celebrating";
    }
    return "happy";
  };

  const handleSend = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    const optimisticUser = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: msg,
      page_context: location.pathname,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimisticUser]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/assistant/chat`, {
        message: msg,
        page_context: location.pathname,
      });
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== optimisticUser.id),
        res.data.user_message,
        res.data.assistant_message,
      ]);
      if (res.data.action) {
        setActionsById((prev) => ({
          ...prev,
          [res.data.action.id]: res.data.action,
        }));
        setActionsList((prev) => [res.data.action, ...prev]);
      }
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Maya teve um problema técnico. Tente de novo daqui a pouquinho.";
      toast.error(detail);
      setMessages((prev) => prev.filter((m) => m.id !== optimisticUser.id));
    } finally {
      setSending(false);
    }
  };

  const handleConfirmAction = async (actionId) => {
    setBusyAction(actionId);
    try {
      const res = await axios.post(
        `${API}/assistant/actions/${actionId}/confirm`,
      );
      setActionsById((prev) => ({ ...prev, [actionId]: res.data }));
      setActionsList((prev) =>
        prev.map((a) => (a.id === actionId ? res.data : a)),
      );
      // Update the assistant message that referenced this action
      setMessages((prev) =>
        prev.map((m) =>
          m.action_id === actionId
            ? { ...m, action_status: res.data.status }
            : m,
        ),
      );
      toast.success("Ação executada!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falhou ao executar.");
      // Refresh the action row so the error is visible
      try {
        const list = await axios.get(`${API}/assistant/actions?limit=10`);
        const updated = (list.data || []).find((a) => a.id === actionId);
        if (updated) {
          setActionsById((prev) => ({ ...prev, [actionId]: updated }));
        }
      } catch {
        /* ignore */
      }
    } finally {
      setBusyAction("");
    }
  };

  const handleCancelAction = async (actionId) => {
    setBusyAction(actionId);
    try {
      const res = await axios.post(
        `${API}/assistant/actions/${actionId}/cancel`,
      );
      setActionsById((prev) => ({ ...prev, [actionId]: res.data }));
      setActionsList((prev) =>
        prev.map((a) => (a.id === actionId ? res.data : a)),
      );
      setMessages((prev) =>
        prev.map((m) =>
          m.action_id === actionId
            ? { ...m, action_status: res.data.status }
            : m,
        ),
      );
      toast.info("Ação cancelada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falhou ao cancelar.");
    } finally {
      setBusyAction("");
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Apagar todo o histórico da sua conversa com a Maya?"))
      return;
    try {
      await axios.delete(`${API}/assistant/history`);
      setMessages([]);
      toast.success("Histórico apagado");
    } catch {
      toast.error("Não consegui apagar o histórico");
    }
  };

  if (!user) return null;

  if (!open) {
    return (
      <button
        type="button"
        data-testid="maya-launcher"
        onClick={() => setOpen(true)}
        aria-label="Abrir assistente Maya"
        title="Fale com a Maya"
        className="fixed bottom-20 right-6 z-50 group p-1 rounded-full bg-white shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-200 ring-2 ring-violet-200 hover:ring-violet-400 print:hidden"
      >
        <div className="relative">
          <MayaAvatar size={44} className="ring-2 ring-violet-300" />
          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-white animate-pulse" />
        </div>
        {/* Tooltip estilo balão, aparece só no hover */}
        <span className="pointer-events-none absolute right-full top-1/2 -translate-y-1/2 mr-3 px-2.5 py-1 rounded-md bg-slate-900 text-white text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 shadow-lg">
          Fale com a Maya
          <span className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-slate-900" />
        </span>
      </button>
    );
  }

  return (
    <div
      data-testid="maya-panel"
      className="fixed bottom-20 right-6 z-50 w-[400px] max-w-[calc(100vw-3rem)] h-[640px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
      role="dialog"
      aria-labelledby="maya-title"
    >
      <div className="px-4 py-3.5 bg-gradient-to-br from-violet-600 via-fuchsia-600 to-pink-600 text-white flex items-center gap-3 shrink-0">
        <div className="relative shrink-0">
          <MayaAvatar size={42} variant={headerVariant} className="ring-2 ring-white/40" />
          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div id="maya-title" className="font-bold text-base tracking-tight leading-tight">
            Maya
          </div>
          <div className="text-[11px] text-white/80 leading-tight">
            Sua copiloto do GridRecruiter · online
          </div>
        </div>
        <button
          type="button"
          data-testid="maya-clear-button"
          onClick={handleClear}
          title="Limpar histórico"
          className="p-1.5 rounded-md hover:bg-white/15 transition-colors"
          aria-label="Limpar histórico"
        >
          <Trash2 className="w-4 h-4" />
        </button>
        <button
          type="button"
          data-testid="maya-minimize-button"
          onClick={() => setOpen(false)}
          title="Minimizar"
          className="p-1.5 rounded-md hover:bg-white/15 transition-colors"
          aria-label="Minimizar"
        >
          <Minimize2 className="w-4 h-4" />
        </button>
        <button
          type="button"
          data-testid="maya-close-button"
          onClick={() => setOpen(false)}
          title="Fechar"
          className="p-1.5 rounded-md hover:bg-white/15 transition-colors"
          aria-label="Fechar"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 bg-white shrink-0">
        <button
          type="button"
          data-testid="maya-tab-chat"
          onClick={() => setTab("chat")}
          className={`flex-1 px-3 py-2 text-xs font-semibold inline-flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
            tab === "chat"
              ? "border-violet-500 text-violet-700"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <MessageSquare className="w-3.5 h-3.5" />
          Conversa
        </button>
        <button
          type="button"
          data-testid="maya-tab-actions"
          onClick={() => setTab("actions")}
          className={`flex-1 px-3 py-2 text-xs font-semibold inline-flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
            tab === "actions"
              ? "border-violet-500 text-violet-700"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <History className="w-3.5 h-3.5" />
          Ações{" "}
          {actionsList.length > 0 && (
            <span className="bg-violet-100 text-violet-700 text-[10px] font-bold px-1.5 rounded">
              {actionsList.length}
            </span>
          )}
        </button>
      </div>

      {/* Chat tab */}
      {tab === "chat" && (
        <>
          {/* Sticky proactive insight banner — outside the scrollable area
              so it's always visible until dismissed. */}
          <AnimatePresence initial={true}>
            {historyLoaded &&
              !insightDismissed &&
              silentRisk &&
              silentRisk.count > 0 && (
                <motion.div
                  key="silent-insight"
                  data-testid="maya-insight-silent-candidates"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: "easeOut" }}
                  className="shrink-0 bg-gradient-to-br from-amber-50 via-white to-rose-50 border-b-2 border-amber-300 relative overflow-hidden"
                >
                  <div className="px-3 py-3">
                    <button
                      type="button"
                      onClick={dismissInsight}
                      data-testid="maya-insight-dismiss"
                      aria-label="Dispensar alerta"
                      className="absolute top-1.5 right-1.5 text-slate-400 hover:text-slate-700 transition-colors p-1"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                <div className="flex items-start gap-2 mb-2 pr-5">
                  <div className="relative shrink-0">
                    <MayaAvatar size={28} variant="alert" />
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-amber-500 rounded-full ring-2 ring-white flex items-center justify-center">
                      <AlertTriangle className="w-1.5 h-1.5 text-white" strokeWidth={3} />
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-slate-800 leading-snug font-semibold">
                      {silentRisk.count} candidato
                      {silentRisk.count > 1 ? "s" : ""} em risco de no-show
                    </p>
                    <p className="text-[10px] text-slate-600 leading-snug">
                      Não abriram o link · entrevista nas próximas 48h
                    </p>
                  </div>
                </div>
                <ul className="space-y-1.5 max-h-[180px] overflow-y-auto">
                  {silentRisk.at_risk.slice(0, 5).map((c) => {
                    const urgent = c.urgency === "alto";
                    return (
                      <li
                        key={c.interview_id}
                        data-testid={`maya-silent-${c.interview_id}`}
                        className={`bg-white border ${urgent ? "border-rose-300" : "border-amber-200"} rounded-md p-2`}
                      >
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-xs font-semibold text-slate-900 truncate">
                                {c.candidate_name || "—"}
                              </span>
                              <span
                                className={`text-[9px] font-bold uppercase tracking-wider px-1 py-px rounded ${
                                  urgent
                                    ? "bg-rose-100 text-rose-800 border border-rose-300"
                                    : "bg-amber-100 text-amber-800 border border-amber-300"
                                }`}
                              >
                                em{" "}
                                {c.hours_until < 24
                                  ? `${Math.round(c.hours_until)}h`
                                  : `${Math.round(c.hours_until / 24)}d`}
                              </span>
                            </div>
                            <div className="text-[10px] text-slate-500 leading-tight truncate">
                              {c.job_title}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => askMayaToFollowUp(c)}
                            data-testid={`maya-followup-${c.interview_id}`}
                            className="shrink-0 inline-flex items-center gap-0.5 px-2 py-1 rounded bg-violet-600 hover:bg-violet-700 text-white text-[10px] font-semibold transition-colors"
                            title="Maya monta uma mensagem de cobrança"
                          >
                            <Sparkles className="w-2.5 h-2.5" />
                            Cobrar
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {silentRisk.count > 5 && (
                  <button
                    type="button"
                    onClick={() =>
                      handleSend("Lista todos os candidatos em risco de no-show")
                    }
                    data-testid="maya-insight-see-all"
                    className="mt-1.5 w-full text-center text-[10px] text-violet-700 hover:underline font-medium"
                  >
                    Ver todos os {silentRisk.count} →
                  </button>
                )}
                  </div>
                </motion.div>
              )}
          </AnimatePresence>

          <div
            ref={scrollRef}
            data-testid="maya-messages"
            className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4 bg-slate-50 space-y-4"
          >
            {messages.length === 0 && historyLoaded && (
              <div className="bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 border border-violet-100 rounded-2xl p-4 shadow-sm">
                <div className="flex items-start gap-2.5 mb-3">
                  <MayaAvatar size={32} />
                  <p className="text-sm text-slate-700 leading-snug">
                    Oi! Sou a <strong>Maya</strong>, sua copiloto do GridRecruiter ✨
                    <br />
                    Posso te explicar o sistema OU executar ações por você (agendar entrevistas, mudar status, enviar mensagens...). Manda ver!
                  </p>
                </div>
                <div className="space-y-1.5">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid="maya-suggestion"
                      onClick={() => handleSend(s)}
                      disabled={sending}
                      className="w-full text-left text-xs text-slate-700 bg-white border border-slate-200 hover:border-violet-300 hover:bg-violet-50 px-3 py-2 rounded-lg transition-colors disabled:opacity-60"
                    >
                      💬 {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {!historyLoaded && messages.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 text-violet-500 animate-spin" />
              </div>
            )}

            {messages.map((m) => {
              const action = m.action_id ? actionsById[m.action_id] : null;
              return (
                <div
                  key={m.id}
                  data-testid={`maya-msg-${m.role}`}
                  className={`flex gap-2 min-w-0 ${m.role === "user" ? "flex-row-reverse" : "flex-row"}`}
                >
                  {m.role === "assistant" && (
                    <MayaAvatar
                      size={28}
                      variant={variantForMessage(m)}
                      className="mt-0.5"
                    />
                  )}
                  <div className={`max-w-[78%] min-w-0 ${m.role === "user" ? "" : "flex-1"}`}>
                    <div
                      className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed shadow-sm ${
                        m.role === "user"
                          ? "bg-brand-blue text-white rounded-br-sm"
                          : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
                      }`}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content) }}
                    />
                    {action && (
                      <ActionCard
                        action={action}
                        onConfirm={handleConfirmAction}
                        onCancel={handleCancelAction}
                        busy={busyAction === action.id}
                      />
                    )}
                  </div>
                </div>
              );
            })}

            {sending && (
              <div data-testid="maya-typing" className="flex gap-2 flex-row">
                <MayaAvatar size={28} variant="thinking" className="mt-0.5" />
                <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce" />
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce"
                    style={{ animationDelay: "0.15s" }}
                  />
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-bounce"
                    style={{ animationDelay: "0.3s" }}
                  />
                </div>
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            className="p-3 bg-white border-t border-slate-200 shrink-0"
          >
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                data-testid="maya-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={sending}
                placeholder="Pergunta algo ou pede uma ação pra Maya..."
                rows={1}
                className="flex-1 resize-none border border-slate-300 rounded-xl px-3 py-2 text-sm leading-snug focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent max-h-32"
                style={{ minHeight: "40px" }}
              />
              <Button
                type="submit"
                data-testid="maya-send-button"
                disabled={!input.trim() || sending}
                className="bg-gradient-to-br from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white h-10 w-10 p-0 rounded-xl shrink-0 shadow-md disabled:opacity-50"
                aria-label="Enviar"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 text-center">
              A Maya pode cometer erros. Confirme antes de executar ações importantes.
            </p>
          </form>
        </>
      )}

      {/* Actions tab */}
      {tab === "actions" && (
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-slate-50 space-y-2">
          {actionsList.length === 0 ? (
            <div className="text-center py-12 text-sm text-slate-500">
              <Sparkles className="w-6 h-6 text-violet-400 mx-auto mb-2" />
              Nenhuma ação ainda. Peça à Maya para fazer algo!
            </div>
          ) : (
            actionsList.map((a) => (
              <div
                key={a.id}
                data-testid={`maya-action-row-${a.id}`}
                className="bg-white border border-slate-200 rounded-lg p-3 text-xs"
              >
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <div className="font-bold text-slate-900">
                    {TOOL_LABELS[a.tool_name] || a.tool_name}
                  </div>
                  <span
                    className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${
                      STATUS_TONE[a.status] || ""
                    }`}
                  >
                    {STATUS_LABEL[a.status] || a.status}
                  </span>
                </div>
                <div
                  className="text-slate-600 leading-snug"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(a.preview) }}
                />
                {a.error && (
                  <div className="mt-2 text-[10px] text-rose-700">{a.error}</div>
                )}
                {a.status === "executed" && (
                  <div className="mt-2 text-[10px] text-emerald-700 inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    {a.executed_at?.slice(0, 16).replace("T", " ")}
                  </div>
                )}
                {a.status === "pending" && (
                  <div className="flex items-center gap-1.5 mt-2">
                    <Button
                      size="sm"
                      onClick={() => handleConfirmAction(a.id)}
                      disabled={busyAction === a.id}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white h-6 text-[10px] px-2"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Confirmar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCancelAction(a.id)}
                      disabled={busyAction === a.id}
                      className="h-6 text-[10px] px-2"
                    >
                      Cancelar
                    </Button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AIAssistantWidget;
